"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { useNavigation } from "@app/[locale]/(site)/navContext";
import { useMarket } from "@app/_context";
import { Input, Checkbox as NGGCheckbox } from "@components/ngg";
import { CheckIcon } from "@ngg/icons";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { ChangeEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import { SITE_CODE } from "@/config/common";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import type { MarketConfiguration } from "@/config/markets/const";

const SEARCHABLE_THRESHOLD = 20;

type Item = {
  count?: number;
  name: string;
  displayName?: string;
};

type Props = {
  items?: Item[];
  onChange: (event: ChangeEvent<HTMLInputElement>, attribute: string) => void;
  values?: string[];
  attribute: string;
  name?: string;
};

function filterElevateItems(
  items: Item[],
  attribute = "",
  marketConfig: MarketConfiguration,
  commerceSlugPairs?: Record<string, string>,
) {
  if (!commerceSlugPairs) return items;
  if (attribute === "custom.leafCategoryCodes") {
    return items?.filter((item) => Boolean(commerceSlugPairs?.[item.name]));
  }
  if (attribute === "custom.statuses") {
    const key = `${SITE_CODE}_${marketConfig.code}_`;
    return items?.filter((item) => item.name.includes(key));
  }
  return items;
}

export default function Checkbox(props: Props) {
  const t = useTranslate();
  const { market } = useMarket().state;
  const { commerceNamePairs, commerceSlugPairs } = useNavigation().state;
  const { items = [], onChange, values = [], attribute, name } = props;
  const wrapper = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>("");

  const searchable = useMemo(() => {
    if (name === "Category") {
      return false;
    }

    return items.length >= SEARCHABLE_THRESHOLD;
  }, [items, name]);

  const getOptionName = useCallback(
    (_name: string) => {
      const key = `${SITE_CODE}_${market.code}_`;
      const name = _name.replace(key, "");
      return t(name, { strict: true }) || commerceNamePairs?.[name] || name;
    },
    [market.code, commerceNamePairs, t],
  );

  const sortByDisplayedName = useCallback(
    (a: Item, b: Item) =>
      getOptionName(a.name) < getOptionName(b.name) ? -1 : 1,
    [getOptionName],
  );

  const options = useMemo(() => {
    let selection = items;
    if (FEATURE_FLAGS.elevate)
      selection = filterElevateItems(
        items,
        attribute,
        market,
        commerceSlugPairs,
      );
    // Filter items starting with search term, or already selected
    const filteredItems = selection.filter((item) => {
      const { count, name } = item;
      const included = name
        .toString()
        .toLowerCase()
        .includes(search.toLowerCase());
      return (count && count > 0 && included) || values.includes(name);
    });
    if (name === "Category") {
      // Otherwise it will be sorted based on category key, e.g. NG_MÖBEL, NG_TABLES, etc, which causes incorrect sorting
      return filteredItems.sort(sortByDisplayedName);
    }
    return filteredItems;
  }, [
    items,
    name,
    attribute,
    market,
    commerceSlugPairs,
    search,
    values,
    sortByDisplayedName,
  ]);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => wrapper.current,
    estimateSize: () => 28,
    overscan: 10,
  });

  const rows = virtualizer.getVirtualItems();

  return (
    <div>
      {searchable ? (
        <Input
          type="text"
          fullWidth
          placeholder={t("filterSearchPlaceholder")}
          onChange={({ target }) => setSearch(target.value)}
          classNames={{
            wrapper: " md:pr-0 mb-3",
            input:
              "focus-visible:-outline-offset-2 h-8 placeholder:text-base py-0 pl-2 placeholder:text-black border-black",
          }}
        />
      ) : null}
      <div
        ref={wrapper}
        style={{ height: 20 + virtualizer.getTotalSize() }}
        className="max-h-[280px] min-h-[40px] w-full overflow-y-auto overflow-x-hidden pb-2 transition-[height] [contain:strict]"
      >
        <div
          style={{ height: virtualizer.getTotalSize() }}
          className="relative w-full"
        >
          {rows.length === 0 && (
            <p className="text-sm text-grey-200">
              {t("filterSearchNoResults")}
            </p>
          )}

          {rows.map(({ key, index, start }) => {
            const { name, count } = options[index];
            return (
              <div
                className="absolute left-0 top-0 mb-1 w-full"
                style={{
                  transform: `translateY(${start}px)`,
                }}
                data-index={index}
                key={key}
              >
                <NGGCheckbox
                  key={name}
                  name={name}
                  checked={values.includes(name)}
                  onChange={(e) => onChange(e, attribute)}
                  disabled={!count}
                  checkedIcon={<CheckIcon />}
                  classNames={{
                    label:
                      "whitespace-nowrap grow ml-2 flex items-center w-full justify-between overflow-hidden",
                    checkbox: `cursor-pointer ${
                      values.includes(name) ? "text-black" : "text-grey-150"
                    }`,
                    wrapper: "p-0",
                  }}
                >
                  <div className="flex w-full">
                    <span className="mr-[2px] truncate text-base">
                      {getOptionName(name)}
                    </span>
                    <span className="text-gray leading-5 mr-[2px] text-sm">{`(${count})`}</span>
                  </div>
                </NGGCheckbox>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
